import {
  CloseOutlined,
  CodeOffOutlined,
  CodeOutlined,
  ZoomIn,
  ZoomOut,
  FitScreen,
} from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { blueGrey, grey } from "@mui/material/colors";
import mermaid from "mermaid";
import { useEffect, useRef, useState } from "react";
import SelectToggleInput from "../../inputs/SelectToggleInput";
import InputWrapper from "../../inputs/shared/InputWrapper";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import MermaidChartEditor from "./MermaidChartEditor";

// Initialize mermaid with configuration
mermaid.initialize({
  startOnLoad: true,
  theme: "default",
  securityLevel: "loose",
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: "basis",
  },
});

const MermaidChart = ({ chart, title, onChartChange }) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedChart, setEditedChart] = useState(chart);
  const [lastValidChart, setLastValidChart] = useState(chart);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setEditedChart(chart);
    setLastValidChart(chart);
  }, [chart]);

  useEffect(() => {
    const renderChart = async () => {
      if (!chartRef.current || !editedChart) return;

      try {
        setError(null);
        const uniqueId = `mermaid-${Date.now()}`;

        // Generate the chart
        const { svg } = await mermaid.render(uniqueId, editedChart);
        chartRef.current.innerHTML = svg;
        setLastValidChart(editedChart);
      } catch (err) {
        console.error("Mermaid rendering error:", err);
        setError(err.message);
      }
    };

    renderChart();
  }, [editedChart]);

  // Handle mouse wheel zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY * -0.01;
        const newScale = Math.min(Math.max(0.1, scale + delta), 5);
        setScale(newScale);
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [scale]);

  // Handle mouse drag for panning
  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.1));
  };

  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleApplyChanges = () => {
    if (onChartChange) {
      onChartChange(editedChart);
    }
    setIsEditing(false);
  };

  const handleResetChanges = () => {
    setEditedChart(lastValidChart);
    setError(null);
  };

  const downloadAsSVG = () => {
    const svgElement = chartRef.current?.querySelector("svg");
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${title || "mermaid-chart"}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAsPNG = () => {
    const svgElement = chartRef.current?.querySelector("svg");
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    const svgRect = svgElement.getBoundingClientRect();
    canvas.width = svgRect.width * 2;
    canvas.height = svgRect.height * 2;

    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${title || "mermaid-chart"}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    };

    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  const copyToClipboard = async () => {
    const svgElement = chartRef.current?.querySelector("svg");
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    const svgRect = svgElement.getBoundingClientRect();
    canvas.width = svgRect.width * 2;
    canvas.height = svgRect.height * 2;

    img.onload = async () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": blob }),
          ]);
          alert("Chart copied to clipboard!");
        } catch (err) {
          console.error("Failed to copy:", err);
          alert("Failed to copy to clipboard");
        }
      });
    };

    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Flx column sx={{ height: "100%" }}>
      <Flx jw jb ac sx={{ mb: 1, flexGrow: 0 }}>
        {title && (
          <Txt fontSize={16} sx={{ lineHeight: 1 }} bold center>
            {title}
          </Txt>
        )}
        <Flx ac g={0.5}>
          <Tooltip title="Zoom In">
            <IconButton size="small" onClick={handleZoomIn} disabled={!!error}>
              <ZoomIn fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton size="small" onClick={handleZoomOut} disabled={!!error}>
              <ZoomOut fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Zoom">
            <IconButton
              size="small"
              onClick={handleResetZoom}
              disabled={!!error}
            >
              <FitScreen fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Tooltip title={isEditing ? "View Mode" : "Edit Syntax"}>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                isEditing ? (
                  <CodeOffOutlined className="thin" fontSize="small" />
                ) : (
                  <CodeOutlined className="thin" fontSize="small" />
                )
              }
              onClick={() => setIsEditing(!isEditing)}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Download as SVG">
            <Button
              size="small"
              onClick={downloadAsSVG}
              disabled={!!error}
              startIcon={<DownloadIcon fontSize="small" />}
            >
              SVG
            </Button>
          </Tooltip>
          <Tooltip title="Download as PNG">
            <Button
              size="small"
              onClick={downloadAsPNG}
              disabled={!!error}
              startIcon={<DownloadIcon fontSize="small" />}
            >
              PNG
            </Button>
          </Tooltip>
          {/* <Tooltip title="Copy to clipboard">
            <IconButton
              size="small"
              onClick={copyToClipboard}
              disabled={!!error}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip> */}
        </Flx>
      </Flx>

      <Flx g={3} sx={{ flexGrow: 1, overflowY: "auto" }}>
        {isEditing && (
          <MermaidChartEditor
            onApplyChanges={handleApplyChanges}
            onReset={handleResetChanges}
            editedChart={editedChart}
            setEditedChart={setEditedChart}
            onClose={() => setIsEditing(false)}
          />
        )}
        {error ? (
          <Paper
            sx={{ p: 2, bgcolor: "error.light", color: "error.contrastText" }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, flexGrow: 1, mb: 1 }}
            >
              Error rendering chart:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {error}
            </Typography>
          </Paper>
        ) : (
          <Card
            ref={containerRef}
            variant="outlined"
            sx={{
              border: `1px solid ${grey[300]}`,
              boxShadow: "none",
              p: 2,
              flexGrow: 1,
              overflow: "hidden",
              cursor: isDragging ? "grabbing" : "grab",
              position: "relative",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div
              className="mermaid"
              ref={chartRef}
              style={{
                display: "flex",
                justifyContent: "center",
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transformOrigin: "center",
                transition: isDragging ? "none" : "transform 0.1s ease-out",
              }}
            />
          </Card>
        )}
      </Flx>
    </Flx>
  );
};

export default MermaidChart;
